import config from "@Config/index";
import { AppThunk } from "@Store/index";
import axios from "axios";
import { getSearchFailure, getSearchRequesting, getSearchSuccess } from "./searchPageReducer";
import { getAllowedFilters, getSearchLink } from "./helper";
import formatFilterState from "../utils/formatFilterState";
import fetchInventorisData from "./fetchInventoriesData";
import fetchRegionListData from "./fetchRegionListData";
import {
  addConfigurableComponents,
  updateCountryCode,
  updateLocality,
  updateRegionId,
} from "@App/appReducer";
import urlBuilder from "@Utils/urlBuilder";
import { isDemoWhitelabelSubdomain } from "constants/index";
import customWindowLocation from "@Utils/customWindowLocation";
import { resetSmartFilterState } from "../components/FilterSection/components/SmartFilters/smartFilterReducer";

const fetchSearchData =
  ({
    canonical_name,
    filters,
    href = "",
    staticContext = null,
    forceFetch = false,
    selectedLang,
    page = 1,
    user_flow_token = "",
    user_auth_token = "",
    initialWhitelabelFiltersSet = false,
    searchSortKey = "relevance_sorting_v2",
    skipScroll = false,
    reqCookies = null, // Add reqCookies parameter for SSR
  }: any): AppThunk =>
  async (dispatch, getState: any) => {
    dispatch(getSearchRequesting(canonical_name));
    dispatch(resetSmartFilterState());

    const userCurrency = getState()?.appData?.userCurrency;
    try {
      const language = selectedLang || getState()?.appData?.userLanguage;
      const regionRes = await axios.get(`${config.API_URL}/api/v0/regions/${canonical_name}`, {
        params: {
          methods:
            "parent_name,ancestors_short,root_name,parent_canonical_name,stories,region_inventory_price_range,application_configs,amber_sales,quick_university_links",
          location_place_id: filters.location_place_id,
          lang: language || "en",
          ...(userCurrency ? { user_currency: userCurrency } : {}),
          use_java_api: true,
        },
        withCredentials: true,
        ...config.apiParams,
      });
      const region = regionRes.data.data;
      if (region?.application_configs) {
        const application_configs_obj = region?.application_configs?.[0];
        dispatch(addConfigurableComponents([{ ...application_configs_obj, regionId: region?.id }]));
      }
      dispatch(updateLocality({ locality: region?.location?.locality?.long_name }));
      dispatch(updateRegionId({ regionId: region?.id }));

      // For country-level regions(abbreviation is used)
      const countryCode =
        region?.region_type === "country"
          ? region?.abbreviation
          : region?.location?.country?.short_name;

      dispatch(updateCountryCode({ countryCode }));
      const whiteListedFilters = getAllowedFilters(filters);

      if (region.region_type === "establishment") {
        whiteListedFilters.filters.university = region.canonical_name;
      }
      if (region.region_type === "sublocality") {
        whiteListedFilters.filters.locality = region.canonical_name;
      }
      const countrySearchRegionTypes = ["country", "state"];
      const isSearchRegion = countrySearchRegionTypes.includes(region.region_type);
      if (initialWhitelabelFiltersSet && !isSearchRegion) {
        // set the url with sort query params
        const refinedFilters = { ...whiteListedFilters.filters };
        if (refinedFilters?.university) delete refinedFilters.university;
        if (refinedFilters?.locality) delete refinedFilters.locality;
        staticContext.url = getSearchLink(canonical_name, refinedFilters);
        if (isDemoWhitelabelSubdomain(getState().whitelabelData.subdomain)) {
          const { partner_uuid, university_mock } = getState().whitelabelData;
          staticContext.url = urlBuilder(staticContext.url, {
            params: {
              partner_id: partner_uuid,
              ...(university_mock && { universityMock: "true" }),
            },
          });
        }
      }

      // note --> need to remove this
      let regionResNew: any = {};
      if (isSearchRegion) {
        regionResNew = await axios.get(`${config.API_URL}/api/v0/regions/${canonical_name}`, {
          params: {
            lang: language || "en",
            use_java_api: true,
          },
        });
      }

      dispatch(
        getSearchSuccess({
          region,
          filters: formatFilterState(whiteListedFilters.filters),
          canonical_url: getSearchLink(canonical_name, whiteListedFilters.filters),
        }),
      );

      if (region?.state != "active") {
        if (region?.parent_canonical_name && region?.parent_id) {
          if (staticContext) {
            staticContext.url = `/places/search/${region?.parent_canonical_name}`;
          }
          if (__CLIENT__) {
            customWindowLocation({ url: `/places/search/${region?.parent_canonical_name}` });
          }
        }
        return null;
      }

      if (staticContext && canonical_name !== region?.canonical_name) {
        const redirectUrl = getSearchLink(region?.canonical_name, {});
        staticContext.url = redirectUrl;
        if (__CLIENT__) {
          customWindowLocation({ url: redirectUrl });
        } else {
          return null;
        }
      }

      let sortKey = searchSortKey;
      // const sortingExperimentKey = searchSortingKey || getCookie("search_sorting_key_v2");
      // const sortingExperimentKey50 = searchSortingKey50 || getCookie("search_sorting_key_v2_50");
      // const regionId = region?.id;
      // const regionParentId = region?.parent_id;
      // const rootId =
      //   region?.ancestors_short?.length > 0 ? region?.ancestors_short?.slice(-1)?.[0]?.id : "";
      // const sortingAlgoV2RegionIds = SORTING_ALGO_V2_REGION_IDS;
      // const sortingAlgoV2RegionIds50 = SORTING_ALGO_V2_REGION_IDS_50;
      // const isInV2RegionIds =
      //   sortingAlgoV2RegionIds.includes(Number(regionId)) ||
      //   sortingAlgoV2RegionIds.includes(Number(regionParentId));
      // const isInV2RegionIds50 =
      //   sortingAlgoV2RegionIds50.includes(Number(regionId)) ||
      //   sortingAlgoV2RegionIds50.includes(Number(regionParentId));
      // const shouldApplyV2Sorting =
      //   (rootId == 1 || rootId == 5 || isInV2RegionIds50) && sortingExperimentKey50 === "TG1";

      if (
        region.region_type === "establishment" ||
        region.region_type === "sublocality" ||
        "locality" in filters
      ) {
        sortKey = "relevance_v0";
      } else {
        sortKey = "relevance_sorting_v2";
      }

      if (["locality", "sublocality", "establishment"].includes(region.region_type)) {
        return dispatch(
          fetchInventorisData({
            canonical_name,
            href,
            filters,
            region,
            forceFetch,
            page,
            user_flow_token,
            user_auth_token,
            selectedLang,
            searchSortKey: sortKey,
            skipScroll, // Pass skipScroll flag to prevent scrolling during backward navigation
            reqCookies, // Pass request cookies for SSR
          }),
        );
      }
      if (["country", "state"].includes(region.region_type)) {
        const regionMeta = {
          ...region.meta,
          ...(regionResNew?.data ? regionResNew?.data?.data?.meta : {}),
          hideDefaultDescription: false,
        };
        return dispatch(
          fetchRegionListData({
            region: {
              ...region,
              meta: regionMeta,
            },
            canonical_name,
            selectedLang,
          }),
        );
      }
      return null;
    } catch (err) {
      return dispatch(getSearchFailure(err));
    }
  };

export default fetchSearchData;
